"use client";

import { useEffect } from "react";
import { aplicarAppZoom } from "@/lib/app-zoom";
import { buscarJsonComCache } from "@/lib/fetch-cache-cliente";
import { lerTemaSalvo, normalizarPapelTema } from "@/lib/tema-usuario";
import {
  HEADER_COLOR_TOKENS,
  SIDEBAR_COLOR_TOKENS,
  THEME_APPEARANCE_COMMAND_EVENT,
  THEME_APPEARANCE_CSS_VARIABLES,
  THEME_APPEARANCE_LEGACY_VARIABLES,
  THEME_APPEARANCE_STORAGE_KEY,
  THEME_APPEARANCE_STORAGE_KEY_V2,
  lerThemeAppearanceOverrides,
  mesclarThemeAppearanceOverrides,
  normalizarThemeAppearanceOverrides,
  salvarThemeAppearanceV2,
  type ThemeAppearanceCommand,
  type ThemeAppearanceOverrides,
  type ThemeAppearanceToken,
} from "@/lib/theme-appearance";

const PAPEL_AUTENTICADO_KEY = "bp-auth-papel";
const SCOPED_VARIABLES_TO_CLEAR = ["--surface-card"];
const HEADER_TOKENS = new Set<ThemeAppearanceToken>(HEADER_COLOR_TOKENS);
const SIDEBAR_TOKENS = new Set<ThemeAppearanceToken>(SIDEBAR_COLOR_TOKENS);

function lerPapelSalvo() {
  if (typeof window === "undefined") return null;
  return normalizarPapelTema(window.localStorage.getItem(PAPEL_AUTENTICADO_KEY));
}

function bloquearDarkNoLogin(papel?: string | null) {
  return window.location.pathname === "/login" && (papel === "GESTOR" || papel === "ADM");
}

function salvarPapelConfirmado(papel?: string | null) {
  const papelNormalizado = normalizarPapelTema(papel);
  if (papelNormalizado) window.localStorage.setItem(PAPEL_AUTENTICADO_KEY, papelNormalizado);
  return papelNormalizado;
}

type StyledElement = HTMLElement | SVGElement;
type AppliedStyles = Map<StyledElement, Set<string>>;

function elementosPorSeletores(...seletores: string[]) {
  const elementos = new Set<HTMLElement>();
  for (const seletor of seletores) {
    document.querySelectorAll<HTMLElement>(seletor).forEach((elemento) => elementos.add(elemento));
  }
  return [...elementos];
}

function registrarEstilo(aplicados: AppliedStyles, elemento: StyledElement, propriedade: string, valor: string) {
  elemento.style.setProperty(propriedade, valor);
  const propriedades = aplicados.get(elemento) ?? new Set<string>();
  propriedades.add(propriedade);
  aplicados.set(elemento, propriedades);
}

function limparEstilosAplicados(aplicados: AppliedStyles) {
  for (const [elemento, propriedades] of aplicados) {
    for (const propriedade of propriedades) elemento.style.removeProperty(propriedade);
  }
  aplicados.clear();
}

function limparResiduosDeAparenciaNaRaiz() {
  const raiz = document.documentElement;
  for (const variavel of Object.values(THEME_APPEARANCE_CSS_VARIABLES)) raiz.style.removeProperty(variavel);
  for (const variaveis of Object.values(THEME_APPEARANCE_LEGACY_VARIABLES)) {
    for (const variavel of variaveis ?? []) raiz.style.removeProperty(variavel);
  }
  for (const variavel of SCOPED_VARIABLES_TO_CLEAR) raiz.style.removeProperty(variavel);
}

function aplicarOverrideGlobal(token: ThemeAppearanceToken, valor: string, aplicados: AppliedStyles) {
  const escopos = [document.documentElement, ...elementosPorSeletores(".bp-desktop-app")];
  for (const escopo of escopos) {
    registrarEstilo(aplicados, escopo, THEME_APPEARANCE_CSS_VARIABLES[token], valor);
    for (const variavel of THEME_APPEARANCE_LEGACY_VARIABLES[token] ?? []) {
      registrarEstilo(aplicados, escopo, variavel, valor);
    }
  }
}

function aplicarOverrideSidebar(token: ThemeAppearanceToken, valor: string, aplicados: AppliedStyles) {
  const sidebars = elementosPorSeletores(
    '[data-user-desktop-sidebar="true"]',
    '[data-user-mobile-navigation="true"]',
  );
  for (const sidebar of sidebars) registrarEstilo(aplicados, sidebar, THEME_APPEARANCE_CSS_VARIABLES[token], valor);

  if (token === "sidebar-brand-bg" || token === "sidebar-brand-text") {
    for (const marca of elementosPorSeletores('[data-user-desktop-brand="true"]')) {
      registrarEstilo(aplicados, marca, THEME_APPEARANCE_CSS_VARIABLES[token], valor);
      registrarEstilo(aplicados, marca, token === "sidebar-brand-bg" ? "background-color" : "color", valor);
    }
  }

  if (token === "sidebar-text" || token === "sidebar-active-text" || token === "sidebar-icon" || token === "sidebar-active-icon") {
    for (const sidebar of sidebars) {
      for (const link of sidebar.querySelectorAll<HTMLAnchorElement>("a")) {
        const ativo = link.getAttribute("aria-current") === "page";
        const aplicarTexto = (token === "sidebar-active-text" && ativo) || (token === "sidebar-text" && !ativo);
        if (aplicarTexto) registrarEstilo(aplicados, link, "color", valor);

        const aplicarIcone = (token === "sidebar-active-icon" && ativo) || (token === "sidebar-icon" && !ativo);
        if (aplicarIcone) {
          link.querySelectorAll<SVGElement>("svg").forEach((icone) => registrarEstilo(aplicados, icone, "color", valor));
        }
      }
    }
  }
}

function aplicarOverrideHeader(token: ThemeAppearanceToken, valor: string, aplicados: AppliedStyles) {
  for (const header of elementosPorSeletores('[data-user-header="true"]', '[data-user-header-shell="true"]')) {
    registrarEstilo(aplicados, header, THEME_APPEARANCE_CSS_VARIABLES[token], valor);
  }
}

function aplicarOverridesNoDocumento(overrides: ThemeAppearanceOverrides, aplicados: AppliedStyles) {
  limparEstilosAplicados(aplicados);
  limparResiduosDeAparenciaNaRaiz();

  const raiz = document.documentElement;
  delete raiz.dataset.fzAppearance;
  if (Object.keys(overrides).length === 0) return;

  for (const [token, valor] of Object.entries(overrides)) {
    if (!valor) continue;
    const tokenNormalizado = token as ThemeAppearanceToken;
    if (HEADER_TOKENS.has(tokenNormalizado)) aplicarOverrideHeader(tokenNormalizado, valor, aplicados);
    else if (SIDEBAR_TOKENS.has(tokenNormalizado)) aplicarOverrideSidebar(tokenNormalizado, valor, aplicados);
    else aplicarOverrideGlobal(tokenNormalizado, valor, aplicados);
  }

  raiz.dataset.fzAppearance = "overrides";
}

function aplicarTema(
  papel: string | null,
  savedOverrides: ThemeAppearanceOverrides,
  previewOverrides: ThemeAppearanceOverrides,
  aplicados: AppliedStyles,
) {
  if (typeof window === "undefined") return;

  if (papel) {
    document.documentElement.dataset.themeRole = papel;
  } else {
    delete document.documentElement.dataset.themeRole;
  }

  const tema = lerTemaSalvo(papel);

  if (tema === "escuro" && !bloquearDarkNoLogin(papel)) {
    document.documentElement.dataset.theme = "dark";
    aplicarOverridesNoDocumento({}, aplicados);
    return;
  }

  delete document.documentElement.dataset.theme;
  aplicarOverridesNoDocumento(mesclarThemeAppearanceOverrides(savedOverrides, previewOverrides), aplicados);
}

export function ThemeController() {
  useEffect(() => {
    let ativo = true;
    let papelAtual: string | null = lerPapelSalvo();
    let savedOverrides: ThemeAppearanceOverrides = {};
    let previewOverrides: ThemeAppearanceOverrides = {};
    const aplicados: AppliedStyles = new Map();

    const carregarOverrides = () => {
      const resultado = lerThemeAppearanceOverrides();
      savedOverrides = resultado.source === "v2-invalid" ? {} : resultado.overrides;
    };

    const sincronizar = () => {
      aplicarTema(papelAtual, savedOverrides, previewOverrides, aplicados);
      aplicarAppZoom();
    };
    const sincronizarZoom = () => aplicarAppZoom();

    const sincronizarPapelSalvo = () => {
      papelAtual = lerPapelSalvo();
      previewOverrides = {};
      sincronizar();
    };

    const tratarComandoAparencia = (event: Event) => {
      const command = (event as CustomEvent<ThemeAppearanceCommand>).detail;
      if (!command || typeof command.action !== "string") return;

      if (command.action === "preview") {
        const overrides = normalizarThemeAppearanceOverrides(command.overrides);
        if (!overrides) return;
        previewOverrides = overrides;
        sincronizar();
        return;
      }

      if (command.action === "save") {
        const overrides = normalizarThemeAppearanceOverrides(command.overrides);
        if (!overrides) return;
        const merged = mesclarThemeAppearanceOverrides(savedOverrides, previewOverrides, overrides);
        const salva = salvarThemeAppearanceV2(merged);
        if (!salva) return;
        savedOverrides = salva.overrides;
        previewOverrides = {};
        sincronizar();
        return;
      }

      if (command.action === "cancel") {
        previewOverrides = {};
        sincronizar();
        return;
      }

      if (command.action === "restore") {
        const salva = salvarThemeAppearanceV2({});
        if (!salva) return;
        savedOverrides = {};
        previewOverrides = {};
        sincronizar();
        return;
      }

      carregarOverrides();
      previewOverrides = {};
      sincronizar();
    };

    const tratarMudancaTemaLegada = (event: Event) => {
      const papelSolicitado = normalizarPapelTema((event as CustomEvent<{ papel?: string | null }>).detail?.papel);
      if (papelSolicitado) papelAtual = papelSolicitado;
      sincronizar();
    };

    const carregarPapel = (tentativa = 0) => {
      buscarJsonComCache<{ papel?: string }>("/api/me", { ttl: 120_000 })
        .then((dados) => {
          if (!ativo) return;
          papelAtual = salvarPapelConfirmado(dados?.papel);
          sincronizar();
        })
        .catch(() => {
          if (ativo && tentativa < 2) window.setTimeout(() => carregarPapel(tentativa + 1), 300);
        });
    };

    const sincronizarArmazenamento = (event: StorageEvent) => {
      if (event.key !== null && event.key !== THEME_APPEARANCE_STORAGE_KEY && event.key !== THEME_APPEARANCE_STORAGE_KEY_V2) return;
      carregarOverrides();
      previewOverrides = {};
      sincronizar();
    };

    carregarOverrides();
    carregarPapel();
    sincronizar();

    const observer = new MutationObserver(() => {
      if (Object.keys(savedOverrides).length > 0 || Object.keys(previewOverrides).length > 0) sincronizar();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    window.addEventListener("resize", sincronizar);
    window.addEventListener("storage", sincronizarArmazenamento);
    window.addEventListener("bp-theme-change", tratarMudancaTemaLegada);
    window.addEventListener("bp-zoom-change", sincronizarZoom);
    window.addEventListener("bp-auth-role-change", sincronizarPapelSalvo);
    window.addEventListener(THEME_APPEARANCE_COMMAND_EVENT, tratarComandoAparencia);

    return () => {
      ativo = false;
      observer.disconnect();
      limparEstilosAplicados(aplicados);
      limparResiduosDeAparenciaNaRaiz();
      delete document.documentElement.dataset.fzAppearance;
      window.removeEventListener("resize", sincronizar);
      window.removeEventListener("storage", sincronizarArmazenamento);
      window.removeEventListener("bp-theme-change", tratarMudancaTemaLegada);
      window.removeEventListener("bp-zoom-change", sincronizarZoom);
      window.removeEventListener("bp-auth-role-change", sincronizarPapelSalvo);
      window.removeEventListener(THEME_APPEARANCE_COMMAND_EVENT, tratarComandoAparencia);
    };
  }, []);

  return null;
}
